import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import {
  RfiNotificationsQuery,
  RfiNotificationsQueryResult,
  useRfiNotificationsQuery,
} from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  RFINotificationEvent,
  RFINotificationEventItem,
} from '@cims-online/shared'
import { useProjectContext } from 'context/ProjectContext'

export type RFINotification =
  RfiNotificationsQuery['rfiNotifications']['records'][0]
type RFINotificationsContext = Pick<
  RfiNotificationsQueryResult,
  'loading' | 'refetch'
> & {
  data: {
    rfiResponseDisciplines: RfiNotificationsQuery['rfiResponseDisciplines']['records']
    rfiNotifications: RfiNotificationsQuery['rfiNotifications']['records']
    requestsForInformation: RfiNotificationsQuery['requestsForInformation']['records']
  }
  recipients: UseRecipientsFn<RFINotification['recipients']>
  responderEvents: Readonly<RFINotificationEventItem>[]
}

const { Provider, useContextFn } = createStrictContext<RFINotificationsContext>(
  'RFINotificationsContext'
)

export const useRFINotificationsContext = useContextFn

const RFINotificationsProvider: React.FC = ({ children }) => {
  const { projectId } = useProjectContext()
  const { data, loading, refetch } = useRfiNotificationsQuery({
    ...DefaultQueryHookOptions,
    variables: { projectId },
  })
  const recipients = useRecipientsFn({
    data,
    getter: (id, { rfiNotifications }) =>
      rfiNotifications.records.find(
        ({ notificationEventId }) => id === notificationEventId
      )?.recipients,
    fallback: {
      __typename: 'RFINotificationRecipients',
      every: null,
      byResponseDiscipline: [],
    },
  })
  return (
    <Provider
      value={{
        loading,
        refetch,
        data: {
          requestsForInformation: data?.requestsForInformation.records ?? [],
          rfiNotifications: data?.rfiNotifications.records ?? [],
          rfiResponseDisciplines: data?.rfiResponseDisciplines.records ?? [],
        },
        recipients,
        responderEvents: [
          RFINotificationEvent.Submitted,
          RFINotificationEvent.Response,
        ],
      }}
    >
      {children}
    </Provider>
  )
}

export default RFINotificationsProvider
